import type { FC, PropsWithChildren, ReactNode } from "react";

import Box from "@mui/material/Box";
import Paper, { type PaperProps } from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";

import { keyframes } from "@emotion/react";
import emotionStyled from "@emotion/styled";
import darkTheme from "@styles/theme/darkThemeOptions";

type DashboardItemProps = {
	title: string | ReactNode;
	isBgAnimated?: boolean;
	content?: string | ReactNode;
	variant?: "outlined";
	background?: string;
};

type DashboardItemExtendedPaperProps = PaperProps & Partial<DashboardItemProps>;

const movingBackgroundAnimation = keyframes`
  0% { background-position: 0% 50% }
  50% { background-position: 100% 50% }
  100% { background-position: 0% 50% }
`;

const ItemOutline = emotionStyled(Paper)`
  position: relative;
  padding: 2px;
  border-radius: 16px;
  box-shadow: ${darkTheme.Shadow.Card};
  background: ${darkTheme.Color.VaporDEXWalletConnect};
` as FC<DashboardItemExtendedPaperProps>;

const ItemOutlineAnimated = emotionStyled(ItemOutline)`
  background-size: 400% 400%;
  animation: ${movingBackgroundAnimation} 6s ease infinite;
` as FC<DashboardItemExtendedPaperProps>;

const Item = styled(Box)(({ theme }) => ({
	borderRadius: 16,
	boxShadow: darkTheme.Shadow.Card,
	padding: theme.spacing(1),
	paddingBottom: 20,
	paddingLeft: 20,
	paddingRight: 20,
	paddingTop: 20,
}));

const ItemBox = styled(Box)(() => ({
	alignItems: "center",
	boxShadow: "unset",
	display: "flex",
	justifyContent: "center",
	marginTop: 2,
}));

const ItemOutlineBox = styled(Box)(() => ({
	background: darkTheme.Color.BackgroundOne,
	borderRadius: 16,
	boxShadow: "unset",
	paddingBottom: 16,
	paddingLeft: 16,
	paddingRight: 16,
	paddingTop: 16,
}));

const DashboardItemOutline: FC<PropsWithChildren<DashboardItemProps>> = ({
	background = darkTheme.Color.SeventiesThunder,
	children,
	isBgAnimated,
	title,
}) => {
	const Wrapper = isBgAnimated ? ItemOutlineAnimated : ItemOutline;
	return (
		<Wrapper sx={{ background }}>
			<ItemOutlineBox>
				<Typography fontWeight={500} fontSize="small">
					{title}
				</Typography>
				<ItemBox sx={{ background: darkTheme.Color.BackgroundOne }}>
					{children}
				</ItemBox>
			</ItemOutlineBox>
		</Wrapper>
	);
};

const DashboardItemRegular: FC<PropsWithChildren<DashboardItemProps>> = ({
	background,
	children,
	title,
}) => {
	return (
		<Item sx={{ background }}>
			<Typography fontWeight={500} fontSize="small">
				{title}
			</Typography>
			<ItemBox>{children}</ItemBox>
		</Item>
	);
};

const DashboardItem: FC<PropsWithChildren<DashboardItemProps>> = ({
	background,
	children,
	content,
	isBgAnimated = false,
	title,
	variant,
}) => {
	const Wrapper =
		variant === "outlined" ? DashboardItemOutline : DashboardItemRegular;

	return (
		<Wrapper background={background} title={title} isBgAnimated={isBgAnimated}>
			{!children && content && (
				<Typography
					fontWeight={700}
					variant="h4"
					textAlign="center"
					display="unset"
					marginRight={2}
				>
					{content}
				</Typography>
			)}
			{children}
		</Wrapper>
	);
};

export default DashboardItem;
